import type { ReactElement } from 'react';
import { AIQueriesPage } from '@/pages/ai-queries/AIQueriesPage';
import { CarTagsPage } from '@/pages/car/CarTagsPage';
import { GenerationsPage } from '@/pages/car/GenerationsPage';
import { MarksPage } from '@/pages/car/MarksPage';
import { ModelsPage } from '@/pages/car/ModelsPage';
import { ModificationsPage } from '@/pages/car/ModificationsPage';
import { TypesPage } from '@/pages/car/TypesPage';
import { CryptoPage } from '@/pages/crypto/CryptoPage';
import { AgentsPage } from '@/pages/insurance/AgentsPage';
import { CompaniesPage } from '@/pages/insurance/CompaniesPage';
import { InsurancePage } from '@/pages/insurance/InsurancePage';
import { LoyaltyLevelsPage } from '@/pages/loyalty/LoyaltyLevelsPage';
import { PrivilegesPage } from '@/pages/loyalty/PrivilegesPage';
import { HandbookPage } from '@/pages/marketing/HandbookPage';
import { OrganizationPage } from '@/pages/marketing/OrganizationPage';
import { PartnersPage } from '@/pages/marketing/PartnersPage';
import { PromoActionsPage } from '@/pages/marketing/PromoActionsPage';
import { PromoLogsPage } from '@/pages/marketing/PromoLogsPage';
import { PromoPage } from '@/pages/marketing/PromoPage';
import { PromotionsPage } from '@/pages/marketing/PromotionsPage';
import { UtmPage } from '@/pages/marketing/UtmPage';
import { MetrikaPage } from '@/pages/metrika/MetrikaPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { MarkupsPage } from '@/pages/parts/MarkupsPage';
import { PartsCartsPage } from '@/pages/parts/PartsCartsPage';
import { PartsPage } from '@/pages/parts/PartsPage';
import { PointsPage } from '@/pages/parts/PointsPage';
import { ArticlesPage } from '@/pages/publications/ArticlesPage';
import { CategoriesPage } from '@/pages/publications/CategoriesPage';
import { PressPage } from '@/pages/publications/PressPage';
import { StoriesPage } from '@/pages/publications/StoriesPage';
import { TagsPage } from '@/pages/publications/TagsPage';
import { PrizesPage } from '@/pages/raffle/PrizesPage';
import { RaffleListPage } from '@/pages/raffle/RaffleListPage';

/** Routes of the "list pages" migration batch (same paths as legacy `routes/config.js`). */
import { AIRequestPage } from '@/pages/ai-queries/AIRequestPage';
import { CryptoDetailPage } from '@/pages/crypto/CryptoDetailPage';
import { InsuranceCartPage } from '@/pages/insurance/InsuranceCartPage';
import { LevelCreatePage, LevelEditPage } from '@/pages/loyalty/LevelFormPage';
import { PrivilegeCreatePage, PrivilegeEditPage } from '@/pages/loyalty/PrivilegeFormPage';
import { HandbookPartnerPage } from '@/pages/marketing/HandbookPartnerPage';
import { PromoCreatePage, PromoEditPage } from '@/pages/marketing/PromoFormPage';
import { PartsCartPage } from '@/pages/parts/PartsCartPage';
import { PartsOrderPage } from '@/pages/parts/PartsOrderPage';
import { ArticleCreatePage, ArticleEditPage } from '@/pages/publications/ArticleFormPage';
import { StoryDetailPage } from '@/pages/publications/StoryDetailPage';
import { RaffleViewPage } from '@/pages/raffle/RaffleViewPage';

export const BATCH_ROUTES: Array<{ path: string; element: ReactElement }> = [
  { path: '/marketing/utm', element: <UtmPage /> },
  { path: '/marketing/promo', element: <PromoPage /> },
  { path: '/marketing/partners', element: <PartnersPage /> },
  { path: '/marketing/promo-actions', element: <PromoActionsPage /> },
  { path: '/marketing/organization', element: <OrganizationPage /> },
  { path: '/marketing/logs', element: <PromoLogsPage /> },
  { path: '/marketing/promotions', element: <PromotionsPage /> },
  { path: '/marketing/handbook', element: <HandbookPage /> },
  { path: '/car/marks', element: <MarksPage /> },
  { path: '/car/models', element: <ModelsPage /> },
  { path: '/car/type', element: <TypesPage /> },
  { path: '/car/tags', element: <CarTagsPage /> },
  { path: '/car/generation', element: <GenerationsPage /> },
  { path: '/car/modification', element: <ModificationsPage /> },
  { path: '/crypto', element: <CryptoPage /> },
  { path: '/loyalty/levels', element: <LoyaltyLevelsPage /> },
  { path: '/loyalty/privileges', element: <PrivilegesPage /> },
  { path: '/insurance/agents', element: <AgentsPage /> },
  { path: '/insurance/companies', element: <CompaniesPage /> },
  { path: '/insurance/insurance', element: <InsurancePage /> },
  { path: '/parts/parts', element: <PartsPage /> },
  { path: '/parts/markups', element: <MarkupsPage /> },
  { path: '/parts/carts', element: <PartsCartsPage /> },
  { path: '/parts/points', element: <PointsPage /> },
  { path: '/notifications', element: <NotificationsPage /> },
  { path: '/publications/article', element: <ArticlesPage /> },
  { path: '/publications/categories', element: <CategoriesPage /> },
  { path: '/publications/tags', element: <TagsPage /> },
  { path: '/publications/press', element: <PressPage /> },
  { path: '/publications/stories', element: <StoriesPage /> },
  { path: '/raffle', element: <RaffleListPage /> },
  { path: '/raffle/wheel-prize', element: <PrizesPage /> },
  { path: '/metrika', element: <MetrikaPage /> },
  { path: '/stat/ai-queries', element: <AIQueriesPage /> },
  { path: '/stat/ai-queries/:id', element: <AIRequestPage /> },
  { path: '/marketing/promo/create', element: <PromoCreatePage /> },
  { path: '/marketing/promo/edit/:id', element: <PromoEditPage /> },
  { path: '/marketing/handbook/partner/:id', element: <HandbookPartnerPage /> },
  { path: '/publications/article/create', element: <ArticleCreatePage /> },
  { path: '/publications/article/edit/:id', element: <ArticleEditPage /> },
  { path: '/publications/stories/:id', element: <StoryDetailPage /> },
  { path: '/loyalty/levels/create', element: <LevelCreatePage /> },
  { path: '/loyalty/levels/edit/:id', element: <LevelEditPage /> },
  { path: '/loyalty/privileges/create', element: <PrivilegeCreatePage /> },
  { path: '/loyalty/privileges/edit/:id', element: <PrivilegeEditPage /> },
  { path: '/crypto/:id', element: <CryptoDetailPage /> },
  { path: '/raffle/view/:id', element: <RaffleViewPage /> },
  { path: '/parts/parts/:id', element: <PartsOrderPage /> },
  { path: '/parts/carts/:id', element: <PartsCartPage /> },
  { path: '/insurance/insurance/cart/:id', element: <InsuranceCartPage /> },
];
